import type { IconProps } from "@chakra-ui/react";
import { OnlineDoctorChIcon } from "../../components/icons/OnlineDoctorChIcon";
import { OnlineDoctorDeIcon } from "../../components/icons/OnlineDoctorDeIcon";
import { BsFacebook, BsInstagram, BsLinkedin, BsTwitter } from "react-icons/bs";
import { type Doctor, Locale } from "../types";
import type { IconType } from "react-icons";
import { germanTheme, swissTheme } from "@onlinedoctor-ch/docustyle";

interface TenantFeatures {
  insurances: boolean;
}

export interface TenantConfiguration {
  name: "ch" | "de";
  id: string;
  theme: Record<string, any>;
  features: TenantFeatures;
  enabledPatientLocales: Locale[];
  iconComponent: React.ComponentType<IconProps>;
  googleMapId: string;
  address: {
    companyName: string;
    line1: string;
    line2: string;
  };
  links: {
    homePage: string;
    legal: { name: string; url: { [key: string]: string } }[];
    navigationMenu: { name: string; url: { [key: string]: string } }[];
    social: {
      name: string;
      url: string;
      iconComponent: IconType;
    }[];
    doctorProfileFAQonCMS: { [key: string]: string };
  };
  getDoctorFaqEntries: (doctor: Doctor) => FaqEntry[];
  base_price: number;
  currency: "CHF" | "EUR";
}

const tenantIds = process.env.tenantIds as unknown as Record<string, string>;

const tenantConfigurations: TenantConfiguration[] = [
  {
    name: "ch",
    id: tenantIds.ch,
    theme: swissTheme,
    enabledPatientLocales: [
      Locale.de,
      Locale.en,
      Locale.fr,
      Locale.it,
      Locale["de-ch"],
    ],
    features: {
      insurances: false,
    },
    iconComponent: OnlineDoctorChIcon,
    googleMapId: "b2483305f67f8fa7",
    address: {
      companyName: "OnlineDoctor AG",
      line1: "address.online_doctor_ch_address_line1",
      line2: "address.online_doctor_ch_address_line2",
    },
    links: {
      homePage: "https://www.onlinedoctor.ch",
      navigationMenu: [
        {
          name: "links.dermatologists",
          url: {
            de: "https://www.onlinedoctor.ch/hautaerzte/",
            "de-ch": "https://www.onlinedoctor.ch/hautaerzte/",
            en: "https://www.onlinedoctor.ch/en/dermatologists/",
            fr: "https://www.onlinedoctor.ch/fr/dermatologues/",
            it: "https://www.onlinedoctor.ch/it/dermatologi/",
          },
        },
      ],
      legal: [
        {
          name: "links.site_notice",
          url: {
            de: "https://www.onlinedoctor.ch/impressum/",
            "de-ch": "https://www.onlinedoctor.ch/impressum/",
            en: "https://www.onlinedoctor.ch/en/site-notice/",
            fr: "https://www.onlinedoctor.ch/fr/mentions-legales/",
            it: "https://www.onlinedoctor.ch/it/colophon/",
          },
        },
        {
          name: "links.agb",
          url: {
            de: "https://www.onlinedoctor.ch/agb/",
            "de-ch": "https://www.onlinedoctor.ch/agb/",
            en: "https://www.onlinedoctor.ch/en/gtc/",
            fr: "https://www.onlinedoctor.ch/fr/cgv/",
            it: "https://www.onlinedoctor.ch/it/cgc/",
          },
        },
        {
          name: "links.data_protection_online_doctor_ag",
          url: {
            de: "https://www.onlinedoctor.ch/datenschutzerklaerung/",
            "de-ch": "https://www.onlinedoctor.ch/datenschutzerklaerung/",
            en: "https://www.onlinedoctor.ch/en/data-protection/",
            fr: "https://www.onlinedoctor.ch/fr/protection-des-donnees/",
            it: "https://www.onlinedoctor.ch/it/protezione-dei-dati/",
          },
        },
        {
          name: "links.medical_device",
          url: {
            de: "https://www.onlinedoctor.ch/medizinprodukt/",
            "de-ch": "https://www.onlinedoctor.ch/medizinprodukt/",
            en: "https://www.onlinedoctor.ch/en/medical-device/",
            fr: "https://www.onlinedoctor.ch/fr/dispositif-medical/",
            it: "https://www.onlinedoctor.ch/it/dispositivo-medico/",
          },
        },
      ],
      social: [
        {
          name: "Facebook",
          url: "https://www.facebook.com/onlinedoctor.ch",
          iconComponent: BsFacebook,
        },
        {
          name: "Instagram",
          url: "https://www.instagram.com/onlinedoctor.ch/",
          iconComponent: BsInstagram,
        },
        {
          name: "linkedin",
          url: "https://www.linkedin.com/company/onlinedoctor/mycompany/",
          iconComponent: BsLinkedin,
        },
      ],
      doctorProfileFAQonCMS: {
        de: "https://www.onlinedoctor.ch/de/faq/",
        en: "https://www.onlinedoctor.ch/en/faq/",
        fr: "https://www.onlinedoctor.ch/fr/faq/",
        it: "https://www.onlinedoctor.ch/it/faq/",
        "de-ch": "https://www.onlinedoctor.ch/de/faq/",
      },
    },
    getDoctorFaqEntries: (_) => [
      {
        question: "doctorProfile.FAQ.what_disease.question",
        answer: "doctorProfile.FAQ.what_disease.answer.ch",
      },
      {
        question:
          "doctorProfile.FAQ.can_i_see_a_dermatologist_without_an_appointment.question",
        answer:
          "doctorProfile.FAQ.can_i_see_a_dermatologist_without_an_appointment.answer",
      },
      {
        question:
          "doctorProfile.FAQ.how_quickly_will_i_receive_a_diagnosis.question",
        answer:
          "doctorProfile.FAQ.how_quickly_will_i_receive_a_diagnosis.answer",
      },
      {
        question: "doctorProfile.FAQ.how_much_does_it_cost.question",
        answer: "doctorProfile.FAQ.how_much_does_it_cost.answer.ch",
      },
    ],
    base_price: 55,
    currency: "CHF",
  },
  {
    name: "de",
    id: tenantIds.de,
    theme: germanTheme,
    features: {
      insurances: true,
    },
    enabledPatientLocales: ["local", "dev"].includes(
      process.env.environment || ""
    )
      ? [Locale.de, Locale.en]
      : [Locale.de],
    iconComponent: OnlineDoctorDeIcon,
    googleMapId: "9c67637200e06020",
    address: {
      companyName: "OnlineDoctor 24 GmbH",
      line1: "address.online_doctor_de_address_line1",
      line2: "address.online_doctor_de_address_line2",
    },
    links: {
      homePage: "https://www.onlinedoctor.de",
      navigationMenu: [
        {
          name: "links.dermatologists",
          url: {
            de: "https://www.onlinedoctor.de/hautaerzte/",
            en: "https://www.onlinedoctor.de/hautaerzte/",
          },
        },
        {
          name: "links.partnerInsurance",
          url: {
            de: "https://www.onlinedoctor.de/partnerversicherungen/",
            en: "https://www.onlinedoctor.de/partnerversicherungen/",
          },
        },
      ],
      legal: [
        {
          name: "links.abb",
          url: {
            de: "https://www.onlinedoctor.de/abb/",
            en: "https://www.onlinedoctor.de/abb/",
          },
        },
        {
          name: "links.agb",
          url: {
            de: "https://www.onlinedoctor.de/agb/",
            en: "https://www.onlinedoctor.de/agb/",
          },
        },
        {
          name: "links.data_protection_dermatologists",
          url: {
            de: "https://www.onlinedoctor.de/datenschutz-dermatologinnen/",
            en: "https://www.onlinedoctor.de/datenschutz-dermatologinnen/",
          },
        },
        {
          name: "links.data_protection_online_doctor",
          url: {
            de: "https://www.onlinedoctor.de/datenschutzerklaerung/",
            en: "https://www.onlinedoctor.de/datenschutzerklaerung/",
          },
        },
        {
          name: "links.site_notice",
          url: {
            de: "https://www.onlinedoctor.de/impressum/",
            en: "https://www.onlinedoctor.de/impressum/",
          },
        },
        {
          name: "links.medical_device",
          url: {
            de: "https://www.onlinedoctor.de/medizinprodukt/",
            en: "https://www.onlinedoctor.de/medizinprodukt/",
          },
        },
      ],
      social: [
        {
          name: "Facebook",
          url: "https://www.facebook.com/onlinedoctor.de/",
          iconComponent: BsFacebook,
        },
        {
          name: "Instagram",
          url: "https://www.instagram.com/onlinedoctor.de/",
          iconComponent: BsInstagram,
        },
        {
          name: "Linkedin",
          url: "https://www.linkedin.com/company/onlinedoctor/mycompany/",
          iconComponent: BsLinkedin,
        },
        {
          name: "Twitter",
          url: "https://twitter.com/OnlinedoctorAG",
          iconComponent: BsTwitter,
        },
      ],
      doctorProfileFAQonCMS: {
        de: "https://www.onlinedoctor.de/faq/",
      },
    },
    getDoctorFaqEntries: (doctor) => [
      {
        question: "doctorProfile.FAQ.what_disease.question",
        answer: "doctorProfile.FAQ.what_disease.answer.de",
      },
      {
        question:
          "doctorProfile.FAQ.can_i_see_a_dermatologist_without_an_appointment.question",
        answer:
          "doctorProfile.FAQ.can_i_see_a_dermatologist_without_an_appointment.answer",
      },
      {
        question:
          "doctorProfile.FAQ.how_quickly_will_i_receive_a_diagnosis.question",
        answer:
          "doctorProfile.FAQ.how_quickly_will_i_receive_a_diagnosis.answer",
      },
      doctor.legal_information?.germany?.is_gkv_member
        ? {
            question:
              "doctorProfile.FAQ.which_health_insurance_cover_the_costs.question",
            answer:
              "doctorProfile.FAQ.which_health_insurance_cover_the_costs.answer",
          }
        : {
            question: "doctorProfile.FAQ.how_much_does_it_cost.question",
            answer: "doctorProfile.FAQ.how_much_does_it_cost.answer.pkv_de",
          },
    ],
    base_price: 38.87,
    currency: "EUR",
  },
];

interface FaqEntry {
  question: string;
  answer: string;
}

const tenantConfigurationsById: { [id: string]: TenantConfiguration } = {};
tenantConfigurations.forEach(
  (config) => (tenantConfigurationsById[config.id] = config)
);

export const getFrontendTenantConfigurationById = (tenantId: string) =>
  tenantConfigurationsById[tenantId];

export const getFrontendTenantConfigurationByName = (tenantName: string) =>
  tenantConfigurations.find((config) => config.name === tenantName);
